import React, { useState } from 'react';
import { Moon, Sun, Menu, X } from 'lucide-react';
import { UserProfile } from '../types';
import './NavigationBar.css';

interface PageVisibility {
  home: boolean;
  championships: boolean;
  drivers: boolean;
  hotlaps: boolean;
}

interface NavigationBarProps {
  activePage: string;
  theme: 'dark' | 'light';
  logoUrl?: string | null;
  user: UserProfile | null;
  pageVisibility?: PageVisibility;
  onToggleTheme: () => void;
  onNavigate: (page: 'home' | 'championships' | 'drivers' | 'hotlap') => void;
  onStartDiscordLogin: () => void;
  onOpenProfilePage: () => void;
  onOpenUserModal: (mode: 'settings') => void;
  onLogout: () => void;
}

const DEFAULT_VISIBILITY: PageVisibility = { home: true, championships: true, drivers: true, hotlaps: true };

const NavigationBar: React.FC<NavigationBarProps> = ({ activePage, theme, logoUrl, user, pageVisibility = DEFAULT_VISIBILITY, onToggleTheme, onNavigate, onStartDiscordLogin, onOpenProfilePage, onOpenUserModal, onLogout }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const handleNavigate = (page: 'home' | 'championships' | 'drivers' | 'hotlap') => {
    setMenuOpen(false);
    onNavigate(page);
  };

  const openProfileAction = (mode: 'settings') => {
    setProfileMenuOpen(false);
    onOpenUserModal(mode);
  };

  return (
    <header className="nav-shell">
      <div className="nav-brand">
        <div className="nav-logo">
          {logoUrl ? <img src={logoUrl} alt="Site logo" /> : 'LOGO'}
        </div>
        <span className="nav-title">ACE Results</span>
      </div>

      <nav className={`nav-links ${menuOpen ? 'open' : ''}`} aria-label="Main navigation">
        {pageVisibility.home && (
          <button className={activePage === 'home' ? 'nav-link active' : 'nav-link'} onClick={() => handleNavigate('home')}>
            Home
          </button>
        )}
        {pageVisibility.championships && (
          <button className={activePage === 'championships' ? 'nav-link active' : 'nav-link'} onClick={() => handleNavigate('championships')}>
            Championships
          </button>
        )}
        {pageVisibility.drivers && (
          <button className={activePage === 'drivers' ? 'nav-link active' : 'nav-link'} onClick={() => handleNavigate('drivers')}>
            Drivers
          </button>
        )}
        {pageVisibility.hotlaps && (
          <button className={activePage === 'hotlap' ? 'nav-link active' : 'nav-link'} onClick={() => handleNavigate('hotlap')}>
            Hotlap
          </button>
        )}
      </nav>

      <div className="nav-actions">
        <button className="nav-toggle theme-toggle" onClick={onToggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {user ? (
          <div className="profile-menu-wrap">
            <button className="profile-trigger" onClick={() => setProfileMenuOpen((s) => !s)} aria-label="Open profile menu">
              {user.avatarUrl ? <img src={user.avatarUrl} alt={user.displayName} /> : <span>{user.displayName.slice(0, 2).toUpperCase()}</span>}
            </button>
            {profileMenuOpen && (
              <div className="profile-menu">
                <button className="profile-menu-item" onClick={() => { setProfileMenuOpen(false); onOpenProfilePage(); }}>Profile</button>
                <button className="profile-menu-item" onClick={() => openProfileAction('settings')}>Profile Settings</button>
                <button className="profile-menu-item" onClick={() => { setProfileMenuOpen(false); onLogout(); }}>Log Out</button>
              </div>
            )}
          </div>
        ) : (
          <button className="nav-toggle discord-login-button" onClick={onStartDiscordLogin} type="button">
            <span className="discord-login-icon" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 127.14 96.36" fill="currentColor">
              <path d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83A97.68 97.68 0 0 0 49 6.83 72.37 72.37 0 0 0 45.64 0a105.89 105.89 0 0 0-26.24 8.07C2.79 33.27-1.71 57.86.54 82.09a105.73 105.73 0 0 0 32.17 16.27 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.84-5.18c.91-.66 1.8-1.35 2.66-2.08a75.57 75.57 0 0 0 64.32 0c.87.73 1.76 1.42 2.66 2.08a68.68 68.68 0 0 1-10.86 5.19 77.25 77.25 0 0 0 6.89 11.1A105.25 105.25 0 0 0 126.6 82.09c2.64-28.08-4.5-52.45-18.9-74.02ZM42.45 65.69c-6.27 0-11.42-5.73-11.42-12.8s5.06-12.8 11.42-12.8c6.41 0 11.52 5.78 11.42 12.8.01 7.07-5.05 12.8-11.42 12.8Zm42.24 0c-6.27 0-11.42-5.73-11.42-12.8s5.06-12.8 11.42-12.8c6.41 0 11.52 5.78 11.42 12.8 0 7.07-5.01 12.8-11.42 12.8Z"/>
            </svg>
            </span>
            <span>Login</span>
          </button>
        )}

        <button className="nav-toggle nav-menu-button" onClick={() => setMenuOpen((s) => !s)} aria-label="Toggle menu">
          {menuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>
    </header>
  );
};

export default NavigationBar;
